import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { DescuentoJudicial } from '../types/descuentoJudicial';
import { DescuentoJudicialService } from '../services/descuentoJudicialService';

interface DescuentoJudicialStore {
  descuentos: DescuentoJudicial[];
  loading: boolean;
  error: string | null;

  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  fetchAll: (idTrabajador?: number, estado?: string) => Promise<void>;
  createDescuento: (descuento: Partial<DescuentoJudicial>) => Promise<DescuentoJudicial>;
  updateDescuento: (descuento: Partial<DescuentoJudicial>) => Promise<DescuentoJudicial>;
  desactivarDescuento: (id: number) => Promise<void>;
}

export const useDescuentoJudicialStore = create<DescuentoJudicialStore>()(
  devtools(
    (set) => ({
      descuentos: [],
      loading: false,
      error: null,

      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),

      fetchAll: async (idTrabajador?, estado?) => {
        set({ loading: true, error: null });
        try {
          const descuentos = await DescuentoJudicialService.getAll(idTrabajador, estado);
          set({ descuentos });
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Error al cargar descuentos judiciales' });
        } finally {
          set({ loading: false });
        }
      },

      createDescuento: async (descuento) => {
        set({ loading: true, error: null });
        try {
          const created = await DescuentoJudicialService.create(descuento);
          const descuentos = await DescuentoJudicialService.getAll();
          set({ descuentos });
          return created;
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Error al crear descuento';
          set({ error: msg });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      updateDescuento: async (descuento) => {
        set({ loading: true, error: null });
        try {
          const updated = await DescuentoJudicialService.update(descuento);
          const descuentos = await DescuentoJudicialService.getAll();
          set({ descuentos });
          return updated;
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Error al actualizar descuento';
          set({ error: msg });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      desactivarDescuento: async (id) => {
        set({ loading: true, error: null });
        try {
          await DescuentoJudicialService.desactivar(id);
          const descuentos = await DescuentoJudicialService.getAll();
          set({ descuentos });
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Error al desactivar descuento' });
          throw err;
        } finally {
          set({ loading: false });
        }
      },
    }),
    { name: 'DescuentoJudicialStore' }
  )
);
